import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Wallet, LogIn, LogOut, ArrowDownCircle, ArrowUpCircle, Printer, Banknote, CreditCard, Landmark, Clock } from "lucide-react";
import { formatCurrency } from "../../lib/utils";

const Kpi = ({ label, value, icon: Icon, tone = "", testId }) => (
  <div className="rounded-lg border bg-card p-3">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
      {Icon && <Icon className="w-3.5 h-3.5" />} {label}
    </div>
    <p className={`font-mono font-bold text-lg mt-1 ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

export const CashSessionPanel = ({ session, onOpen, onClose, onEntry, onExit, onPrint }) => {
  if (!session) {
    return (
      <Card data-testid="cash-closed-card">
        <CardContent className="py-10 flex flex-col items-center text-center gap-3">
          <Wallet className="w-12 h-12 text-muted-foreground/40" />
          <div>
            <p className="font-semibold text-lg">Caixa fechada</p>
            <p className="text-sm text-muted-foreground">Abra a caixa com o fundo inicial para começar a registar vendas, entradas e saídas.</p>
          </div>
          <Button onClick={onOpen} className="gap-2" data-testid="open-cash-btn"><LogIn className="w-4 h-4" /> Abrir Caixa</Button>
        </CardContent>
      </Card>
    );
  }
  const openedAt = new Date(session.opened_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
  const others = session.total_sales - session.total_cash - session.total_card;
  return (
    <Card data-testid="cash-open-card">
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <div>
              <p className="font-semibold">Caixa aberta <span className="text-muted-foreground font-normal text-sm">· {session.session_id}</span></p>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> desde {openedAt} por {session.opened_by_name || "—"}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={onEntry} className="gap-1.5 text-emerald-600 border-emerald-500/40" data-testid="cash-entry-btn"><ArrowDownCircle className="w-4 h-4" /> Entrada</Button>
            <Button variant="outline" size="sm" onClick={onExit} className="gap-1.5 text-red-600 border-red-500/40" data-testid="cash-exit-btn"><ArrowUpCircle className="w-4 h-4" /> Saída</Button>
            <Button variant="outline" size="sm" onClick={onPrint} className="gap-1.5" data-testid="cash-print-session-btn"><Printer className="w-4 h-4" /> Relatório parcial</Button>
            <Button size="sm" variant="destructive" onClick={onClose} className="gap-1.5" data-testid="close-cash-btn"><LogOut className="w-4 h-4" /> Fechar Caixa</Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          <Kpi label="Fundo inicial" value={formatCurrency(session.opening_balance)} icon={Wallet} testId="cash-opening-balance" />
          <Kpi label="Vendas" value={formatCurrency(session.total_sales)} testId="cash-total-sales" />
          <Kpi label="Numerário" value={formatCurrency(session.total_cash)} icon={Banknote} testId="cash-total-cash" />
          <Kpi label="Multicaixa" value={formatCurrency(session.total_card)} icon={CreditCard} testId="cash-total-card" />
          <Kpi label="Outros métodos" value={formatCurrency(others)} icon={Landmark} testId="cash-total-other" />
          <Kpi label="Entradas / Saídas" value={`+${formatCurrency(session.total_entries)} / ${session.total_exits ? "-" : ""}${formatCurrency(session.total_exits)}`} testId="cash-entries-exits" />
          <div className="rounded-lg border-2 border-emerald-500/60 bg-emerald-500/10 p-3">
            <p className="text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Numerário em caixa (esperado)</p>
            <p className="font-mono font-black text-xl text-emerald-700 dark:text-emerald-300" data-testid="cash-expected">{formatCurrency(session.expected_cash)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
